import AppPaths from '../AppPaths';
import * as types from '../types';

export interface ShopProductForJsonLd {
  key: string;
  name: string;
  /** public path of the product image, e.g. `/product_images/mini/device.png` */
  thumb: string;
  sku?: string;
}

/**
 * Product structured data for the pick-your-fi shop.
 *
 * The storefront is client-rendered, so Googlebot is the only crawler that sees this: it renders
 * JavaScript, AI crawlers do not. That is fine here. The marketing site carries the
 * server-rendered Product markup assistants cite; this exists so Google's merchant listings and
 * product rich results see a price and an in-stock offer on the page that actually sells.
 *
 * The offer quotes the list price, which is the figure the card shows as its strikethrough and the
 * one that does not move with whichever promo code the shopper happens to hold. A product whose
 * price has not resolved yet is left out rather than published without an offer, which Google
 * rejects. No `aggregateRating`: the 4.7 on the page is a brand-wide figure, not one per tracker.
 */
export default function buildShopProductsJsonLd(
  products: ShopProductForJsonLd[],
  discountsByKey: Record<string, types.HardwareDiscountPreview | undefined>,
  currency: string,
  origin: string,
): Record<string, unknown> | undefined {
  const items = products
    .filter((product) => discountsByKey[product.key] !== undefined)
    .map((product, index) => {
      const discount = discountsByKey[product.key]!;
      const url = `${origin}${AppPaths.Shop}?selected=${product.key}`;
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          image: `${origin}${product.thumb}`,
          url,
          brand: { '@type': 'Brand', name: 'Fi' },
          ...(product.sku ? { sku: product.sku } : {}),
          offers: {
            '@type': 'Offer',
            price: (discount.listPriceInCents / 100).toFixed(2),
            priceCurrency: currency,
            availability: 'https://schema.org/InStock',
            url,
          },
        },
      };
    });

  if (items.length === 0) {
    return undefined;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items,
  };
}
